import { ethers } from "ethers";
import { Network, Alchemy } from 'alchemy-sdk';
import { bscTestnet, polygon, polygonAmoy, hardhat } from '@reown/appkit/networks';
import bscTestnetDeployment from './deployments/bscTestnet.json';

// Optional Config object, but defaults to demo api-key and eth-mainnet.
const settings = {
  apiKey: 'rH_6eF58MmxmyBy3FGdKCTYvUSnBizxH', // Replace with your Alchemy API Key.
  network: Network.BSC_TESTNET, // Replace with your network.
};

const alchemy = new Alchemy(settings);

// Addresses come from the env file the contracts deploy writes (deployments/<network>.env, generated with
// FRONTEND_ENV_PREFIX=REACT_APP_) copied to this project's .env. On the public BSC testnet (the default chain) they
// come from src/deployments/bscTestnet.json, written from the deploy by the contracts' scripts/dev/sync-frontend.cjs,
// so a plain build of the testnet branch needs no env file. The literals below are the old BSC testnet deployment.
const DEPLOYMENTS = { 97: bscTestnetDeployment };
const fromEnv = (name) => process.env[`REACT_APP_${name}`];
const deployed = DEPLOYMENTS[Number(fromEnv('CHAIN_ID') || 97)] || {};
export const env = (name, fallback) => fromEnv(name) || deployed[name] || fallback;

const NETWORKS = {
  97: { chain: bscTestnet, ws: 'wss://bsc-testnet.drpc.org', wsBackup: 'wss://bsc-testnet-rpc.publicnode.com', rpc: 'https://bsc-testnet.bnbchain.org' },
  137: { chain: polygon, ws: 'wss://polygon-bor-rpc.publicnode.com', wsBackup: 'wss://polygon.drpc.org', rpc: 'https://polygon-bor-rpc.publicnode.com' },
  80002: { chain: polygonAmoy, ws: 'wss://polygon-amoy-bor-rpc.publicnode.com', wsBackup: 'wss://polygon-amoy.drpc.org', rpc: 'https://rpc-amoy.polygon.technology' },
  31337: { chain: hardhat, ws: 'ws://127.0.0.1:8545', wsBackup: 'ws://127.0.0.1:8545', rpc: 'http://127.0.0.1:8545' },
};

export const chainId = Number(env('CHAIN_ID', 97));
const net = NETWORKS[chainId];
if (!net) throw new Error(`REACT_APP_CHAIN_ID ${chainId} is not supported (use ${Object.keys(NETWORKS).join(', ')})`);
export const targetChain = net.chain;
export const rpcUrl = env('RPC_URL', net.rpc);
const wsUrl = env('WS_URL', net.ws);

// Lotteries (daily / weekly)
export const luckBlocksAddress = env('LOTTERY_BTC_DAILY_ADDRESS', "0xBFeC98Cde983769a5f66e41C190944450FB73936");
export const luckBlocksWAddress = env('LOTTERY_BTC_WEEKLY_ADDRESS', "0xa6EBa8a7D06091120F8B6eEe6012031Fd41F64B0");
export const ethLuckBlocksAddress = env('LOTTERY_ETH_DAILY_ADDRESS', "0x1aC35135Dd761bEA734aE3c25C14556D2237dF47");
export const ethluckBlocksWAddress = env('LOTTERY_ETH_WEEKLY_ADDRESS', "0x9d5a347E6b9C60CC3882f0792cAC8797b354a270");
export const maticLuckBlocksAddress = env('LOTTERY_POL_DAILY_ADDRESS', "0x8Ad9A4F67De0e2d20eAF3DC6310E090Ce7Be0d0C");
export const maticLuckBlocksWAddress = env('LOTTERY_POL_WEEKLY_ADDRESS', "0xaa5A3A712C33C57FcAf97423cB1FC45358AB28b7");
export const CRANNILuckBlocksAddress = env('LOTTERY_CRANNI_DAILY_ADDRESS', "0x91CfA49E2677ed5C0175AA2FA41D011ae2c417Ac");
export const CRANNILuckBlocksWAddress = env('LOTTERY_CRANNI_WEEKLY_ADDRESS', "0x84623C56E7E2b33e3321aC4ACd17e4Cc537f4111");
export const MultiCall = env('MULTICALL_ADDRESS', "0xD855c21e038Fe5f6146f480aDeEdD0d96d2212C3");

// Tokens
export const erc20Address = env('TOKEN_USDT_ADDRESS', "0x2a3E6a9824c1f59AEFDe1f5eC4F2b4c3460C6992"); // USDT (6 decimals)
export const usdtContractAddress = erc20Address;
export const wbtcAddress = env('TOKEN_WBTC_ADDRESS', "0xd3CcAd392A8f37B95d9a85327a9808735AcDBd0B");
export const wethAddress = env('TOKEN_WETH_ADDRESS', "0x36E5080F0FC36890571e409E9a6985F70d957D46");
export const wpolAddress = env('TOKEN_WPOL_ADDRESS', "0xae13d989daC2f0dEbFf460aC112a837C89BAa7cd");
export const cranniAddress = env('TOKEN_CRANNI_ADDRESS', "0x8C426Bc4506b4f7BfB21cF67A342e0E7EBe0980f"); // mainnet address : 0x671078C0496Fa135a8c45fC7c9FA7B1501fD5146
// Cranni Bestiarium (animal game): no mainnet deployment yet
export const bestiariumAddress = env('BESTIARIUM_ADDRESS', '');
export const lpToken = env('TOKEN_CRANNI_LP_ADDRESS', "0x68c000A1E9F1DcE62358019716B5300937d2b659");

// Cynths CDP: synths, collaterals and stability pools are read from the CDP itself
export const cdpAddress = env('CDP_ADDRESS', "");
export const cynthsOracleAddress = env('CYNTHS_ORACLE_ADDRESS', "");
// synth/USDT V2 pairs seeded by the deploy (DEX_PAIR_CI_BOLD_ADDRESS...), "" when the synth has no market yet
export const synthDexPair = (ticker) => env(`DEX_PAIR_${ticker.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase()}_ADDRESS`, '');
// swap page of the DEX holding those pairs ({in} and {out} are token addresses)
export const dexSwapUrl = env('DEX_SWAP_URL', 'https://quickswap.exchange/#/swap?inputCurrency={in}&outputCurrency={out}');

export const stakingContract = env('LIQUIDITY_MINING_ADDRESS', "0xCC205BB6B3942348f31Fd0385e2f0f7C11F2d33c");
export const nftNodes = env('NODES_NFT_ADDRESS', "0x49c3844b00AA8268E41959c18D84d56F8De5F9dc");
export const nftNodesCFG = env('NODES_CONFIG_ADDRESS', "0x17Fc1Fe5bCD865d88a03A7031C18cCaFE8fBA427");
// Node council: executes the proposals the staked NFT nodes approve
export const governanceAddress = env('GOVERNANCE_ADDRESS', "");
export const bullvsbear = env('BULL_VS_BEAR_ADDRESS', "0x309cB3b43C0B8Ec64776e4DDf4aa71B971d383bf");
export const lbreferral = env('REFERRAL_ADDRESS', "0x12C5466642CFbE5A50151C834b16Ad4A5C8e9181");

export const wsprovider = new ethers.providers.WebSocketProvider(wsUrl);

export const alchemyBase = alchemy;

export const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
export const bckpProvider = new ethers.providers.WebSocketProvider(env('WS_BACKUP_URL', net.wsBackup));
